import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SettingsMenu.css';

type ThemeMode = 'light' | 'dark' | 'system';

interface SettingsMenuProps {
    themeMode: ThemeMode;
    onThemeChange: (mode: ThemeMode) => void;
    onShowSystemInfo?: () => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
    // themeMode,
    // onThemeChange,
    onShowSystemInfo
}) => {
    const { t, i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const languages = [
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'it', name: 'Italiano', flag: '🇮🇹' }
    ];

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (languageCode: string) => {
        i18n.changeLanguage(languageCode);
        setIsMenuOpen(false);
    };

    return (
        <div className="settings-menu" ref={menuRef}>
            <button
                type="button"
                className="settings-button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                title={t('settings.menu')}
            >
                <span className="material-icons">settings</span>
                <span className="material-icons">
                    {isMenuOpen ? 'expand_less' : 'expand_more'}
                </span>
            </button>

            {isMenuOpen && (
                <div className="settings-dropdown">
                    {/* Language Section */}
                    <div className="settings-section">
                        <div className="settings-section-header">
                            <span className="material-icons">language</span>
                            <span>{t('profile.language')}</span>
                        </div>
                        <div className="language-options">
                            {languages.map((language) => (
                                <button
                                    type="button"
                                    key={language.code}
                                    className={`language-option ${i18n.language === language.code ? 'active' : ''}`}
                                    onClick={() => handleLanguageChange(language.code)}
                                >
                                    <span className="language-flag">{language.flag}</span>
                                    <span className="language-name">{language.name}</span>
                                    {i18n.language === language.code && (
                                        <span className="material-icons">check</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="settings-divider"></div>

                    {/* About Section */}
                    <div className="settings-section">
                        <button
                            type="button"
                            className="settings-menu-item"
                            onClick={() => {
                                onShowSystemInfo?.();
                                setIsMenuOpen(false);
                            }}
                        >
                            <span className="material-icons">info</span>
                            <span>{t('profile.about')}</span>
                            <span className="material-icons action-icon">chevron_right</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsMenu;